import { buildMakeUseAxios } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { IOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";

const axios = buildMakeUseAxios(`${import.meta.env.VITE_MATCHING_SERVICE_URL}`);

export const usePostGenerateTemplates = () => {
  const [state, execute, cancel] = axios(
    {
      url: "/matching/apply/templates",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (data: Record<string, unknown>) => {
      return execute({
        url: "/matching/apply/templates",
        data,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

interface IUsePostGenerateTemplateCallback {
  templateId: string;
  candidate?: ObjectId | ICandidate;
  candidates?: (ObjectId | ICandidate)[];
  offer?: IOffer;
  offers?: IOffer[];
  recruiterText?: string;
}

interface IPostGenerateTemplate {
  subject: string;
  content: string;
}

interface IPostGenerateTemplateBody {
  candidateId?: ObjectId;
  candidateIds?: ObjectId[];
  offer?: IOffer;
  offers?: IOffer[];
  recruiterText?: string;
}

export const usePostGenerateTemplate = () => {
  const [state, execute, cancel] = axios<IPostGenerateTemplate, IPostGenerateTemplateBody, unknown>(
    {
      url: "/matching/apply/template",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    ({ templateId, candidate, offers, offer, candidates, recruiterText }: IUsePostGenerateTemplateCallback) => {
      const candidateId = typeof candidate === "string" ? candidate : candidate?._id;
      const candidateIds = candidates?.map((candidate) => (typeof candidate === "string" ? candidate : candidate._id));
      return execute({
        url: `/matching/apply/template/${templateId}`,
        data: { candidateId, offers, offer, candidateIds, recruiterText },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
